import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';
import { Plus, Minus, Check, ArrowLeft, ShoppingCart, Flame } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ProductPage = () => {
  const { productId } = useParams();
  const [product, setProduct] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(true);
  const { addToCart } = useCart();

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const response = await axios.get(`${API}/products/${productId}`);
        setProduct(response.data);
      } catch (error) {
        console.error('Error fetching product:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [productId]);

  const handleAddToCart = () => {
    if (!product) return;
    addToCart(product, quantity);
    toast.success(`${product.name} added to cart!`, {
      description: `Quantity: ${quantity}`,
    });
  };

  const colorMap = {
    'herb-savory': '#2D5016',
    'citrus-garlic': '#D4540A',
    'smoky-savory': '#5C3D2E',
    'seafood-seasoning': '#1E4D6B',
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAF8F5] flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-[#D4540A] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-[#FAF8F5] flex flex-col items-center justify-center">
        <h1 className="text-2xl font-bold text-[#2A2A2A] mb-4">Product not found</h1>
        <Link to="/shop">
          <Button>Back to Shop</Button>
        </Link>
      </div>
    );
  }

  const accentColor = colorMap[product.id] || '#D4540A';

  return (
    <div className="min-h-screen bg-[#FAF8F5]" data-testid="product-page">
      {/* Breadcrumb */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <Link
          to="/shop"
          className="inline-flex items-center text-sm text-[#666] hover:text-[#2A2A2A] transition-colors"
          data-testid="back-to-shop"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Back to Shop
        </Link>
      </div>

      {/* Product Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
          {/* Full Bottle Image */}
          <div className="aspect-square lg:aspect-[3/4] min-h-[400px] lg:min-h-[550px] rounded-2xl overflow-hidden">
            <img
              src={product.image_url}
              alt={product.name}
              className="w-full h-full object-cover"
              data-testid="product-main-image"
            />
          </div>

          {/* Product Details */}
          <div className="space-y-6">
            <div>
              <p className="text-sm font-semibold uppercase tracking-wider mb-2" style={{ color: accentColor }}>
                {product.subtitle}
              </p>
              <h1 className="font-['Bodoni_Moda'] text-4xl sm:text-5xl font-bold text-[#2A2A2A] mb-4" data-testid="product-title">
                {product.name}
              </h1>
              <p className="text-lg text-[#555]" data-testid="product-description">
                {product.hook}
              </p>
            </div>

            <div className="text-4xl font-bold text-[#2A2A2A]" data-testid="product-price">
              ${product.price.toFixed(2)}
            </div>

            {/* Flavor Profile Pills */}
            <div className="flex flex-wrap gap-2">
              {product.flavor_profile.map((flavor, idx) => (
                <span
                  key={idx}
                  className="px-4 py-2 rounded-full text-sm font-semibold text-white"
                  style={{ backgroundColor: accentColor }}
                >
                  {flavor}
                </span>
              ))}
            </div>

            {/* Quantity & Add to Cart */}
            <div className="flex flex-col sm:flex-row gap-4 pt-2">
              <div className="flex items-center border border-[#E5E0D8] rounded-xl bg-white">
                <button
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  className="p-4 hover:bg-[#F5F3F0] transition-colors rounded-l-xl"
                  data-testid="qty-decrease"
                >
                  <Minus className="w-5 h-5" />
                </button>
                <span className="px-6 font-semibold text-xl" data-testid="qty-value">{quantity}</span>
                <button
                  onClick={() => setQuantity(quantity + 1)}
                  className="p-4 hover:bg-[#F5F3F0] transition-colors rounded-r-xl"
                  data-testid="qty-increase"
                >
                  <Plus className="w-5 h-5" />
                </button>
              </div>
              <Button
                onClick={handleAddToCart}
                className="flex-1 py-6 text-lg font-semibold text-white rounded-xl"
                style={{ backgroundColor: accentColor }}
                data-testid="add-to-cart-btn"
              >
                <ShoppingCart className="w-5 h-5 mr-2" />
                Add to Cart — ${(product.price * quantity).toFixed(2)}
              </Button>
            </div>

            {/* Perfect On - Food Visual Strip */}
            {product.perfect_on && product.perfect_on.length > 0 && (
              <div className="pt-4">
                <div className="flex items-center gap-2 mb-4">
                  <Flame className="w-5 h-5" style={{ color: accentColor }} />
                  <h3 className="font-semibold text-sm uppercase tracking-wider text-[#2A2A2A]">
                    Perfect On
                  </h3>
                </div>
                <div className="grid grid-cols-4 gap-3">
                  {product.perfect_on.map((item, idx) => (
                    <div key={idx} className="text-center group">
                      <div className="aspect-square rounded-xl overflow-hidden mb-2 bg-[#F5F3F0] border border-[#E5E0D8] transition-all group-hover:shadow-md group-hover:border-transparent">
                        <img
                          src={item.image}
                          alt={item.name}
                          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                          loading="lazy"
                        />
                      </div>
                      <span className="text-xs font-medium text-[#555]">{item.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Usage Range & Great For */}
            <div className="grid sm:grid-cols-2 gap-4 pt-4">
              {/* Usage Range */}
              <div className="bg-white rounded-xl p-5 border border-[#E5E0D8]">
                <h3 className="font-semibold text-xs uppercase tracking-wider text-[#888] mb-3">
                  Usage Range
                </h3>
                <div className="space-y-2.5">
                  <div className="flex justify-between text-sm">
                    <span className="font-medium text-[#2A2A2A]">Light</span>
                    <span className="text-[#666]">{product.usage_range.light}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="font-medium text-[#2A2A2A]">Medium</span>
                    <span className="text-[#666]">{product.usage_range.medium}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="font-medium text-[#2A2A2A]">Heavy</span>
                    <span className="text-[#666]">{product.usage_range.heavy}</span>
                  </div>
                </div>
              </div>

              {/* Great For */}
              <div className="bg-white rounded-xl p-5 border border-[#E5E0D8]">
                <h3 className="font-semibold text-xs uppercase tracking-wider text-[#888] mb-3">
                  Great For
                </h3>
                <div className="flex flex-wrap gap-2">
                  {product.great_for.map((item, idx) => (
                    <span
                      key={idx}
                      className="inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-medium bg-[#F5F3F0] text-[#2A2A2A]"
                    >
                      <Check className="w-3.5 h-3.5 mr-1.5" style={{ color: accentColor }} />
                      {item}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Bundle Cross-sell */}
            <div className="bg-gradient-to-r from-[#2A2A2A] to-[#3A3A3A] rounded-xl p-6 text-white mt-4">
              <h3 className="font-['Bodoni_Moda'] text-xl font-bold mb-2">Try the Full Set</h3>
              <p className="text-[#B8B5B0] text-sm mb-4">
                Get all 4 blends and save! Perfect for exploring every flavor.
              </p>
              <div className="flex items-center justify-between">
                <span className="text-2xl font-bold">$29.99</span>
                <Button
                  variant="outline"
                  className="border-white text-white hover:bg-white/10"
                  disabled
                >
                  Coming Soon
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductPage;
